import * as React from "react";
import { Car, DateRanges, EvCarError, EvCarRequest } from "@/models/vehicle";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Title from "@/components/molecules/Title";
import {
  Grid,
  Paper,
  Autocomplete as AutocompleteMUI,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TableHead,
  Box,
} from "@mui/material";
import { Messages } from "@/util/Messages";
import {
  VehicleConstants,
  getSetPreviousCarWarnMessage,
  getVehicleCodeAndEvCarLabel,
  getVehicleCodeLabel,
  lastCollectedAt,
} from "./VehicleConstants";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { TitleGroup } from "@/components/molecules/TitleGroup";
import TextField from "@/components/atoms/TextField";
import { Autocomplete } from "@/components/molecules/AutoComplete";
import { Picker } from "@/components/atoms/Picker";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { Typography } from "@/components/atoms/Typography";
import { getJSTCurrentTime, getShortDate } from "@/util/datetime";
import {
  ChargeCard,
  DriverWorkCycle,
  EvCar,
  EvCarModel,
  GochargeCard,
  GochargeCardWithDefaultStartDate,
  Subsidy,
} from "@/models/common";
import {
  createEvCarAPI,
  editEvCarAPI,
  getCarAPI,
  getLinkedDriverWorkCycles,
  getParkingLotsAPI,
  getReplaceableCarsAPI,
  getSelectablePreviousCars,
} from "@/util/api/vehicle";
import {
  getChargeCardsAPI,
  getDriverWorkCyclesAPI,
  getEvCarModelsAPI,
  getGochargeCardsAPI,
  getLoginUserAPI,
  getSubsidiesAPI,
} from "@/util/api/API";
import { GX_ADMIN } from "@/models/auth";
import { AxiosError } from "axios";
import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import toast from "@/components/hoc/SnackbarAlert";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";
import { OfficeLocation } from "@/models/officeLocation";

const NUMBER_OF_YEAR_WARNING = 6;
const FRONT_APPLY_ON_DEFAULT_ERROR = `${VehicleConstants.FrontApplyOn}${Messages.PleaseEnter}`;

export const VehicleEdit = () => {
  const [evCar, setEvCar] = React.useState<Partial<EvCar>>({
    excludedFromNumberOfCars: false,
  });
  const [workingCopy, setWorkingCopy] = React.useState<EvCar>();

  const [car, setCar] = React.useState<Car | null>(null);
  const [errorData, setErrorData] = React.useState<EvCarError>({});
  const [evCarModels, setEvCarModels] = React.useState<EvCarModel[]>([]);
  const [subsidies, setSubsidies] = React.useState<Subsidy[]>([]);
  const [subsidySelected, setSubSidySelected] = React.useState<Subsidy | null>(
    null
  );
  const [frontDriverWorkCycles, setFrontDriverWorkCycles] = React.useState<
    DriverWorkCycle[]
  >([]);
  const [backDriverWorkCycles, setBackDriverWorkCycles] = React.useState<
    DriverWorkCycle[]
  >([]);
  const [chargeCards, setChargeCards] = React.useState<ChargeCard[]>([]);
  const [chargeCardSelected, setChargeCardSelected] =
    React.useState<ChargeCard | null>(null);
  const [cause, setCause] = React.useState("");

  const [gochargeCardsWithDefault, setGochargeCardsWithDefault] =
    React.useState<GochargeCardWithDefaultStartDate[]>([]);
  const [gochargeCardSelected, setGochargeCardSelected] =
    React.useState<GochargeCard | null>(null);
  const [gochargeCardStartOn, setGochargeCardStartOn] = React.useState("");
  const [gochargeCardEndOn, setGochargeCardEndOn] = React.useState("");
  const [gochargeCardEndInRange, setGochargeCardEndInRange] =
    React.useState(true);
  const [gochargeCardLocked, setGochargeCardLocked] = React.useState(false);

  const gochargeCardOptions = React.useMemo(
    () => gochargeCardsWithDefault.map(({ gochargeCard }) => gochargeCard),
    [gochargeCardsWithDefault]
  );

  const [parkingLots, setParkingLots] = React.useState<OfficeLocation[]>([]);
  const [replaceableCars, setReplaceableCars] = React.useState<Car[]>([]);
  const [previousCars, setPreviousCars] = React.useState<Car[]>([]);

  const [showShortPeriodWarning, setShowShortPeriodWarning] =
    React.useState(false);
  const [showSetPreviousCarWarning, setShowSetPerviousCarWarning] =
    React.useState(false);

  const minUsageStartOn = React.useMemo(() => {
    const lastMonth = new Date(getJSTCurrentTime("date"));
    lastMonth.setDate(1);
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    return lastMonth.toISOString().slice(0, 10);
  }, []);

  const [minUsageEndOn, setMinUsageEndOn] = React.useState(
    getJSTCurrentTime("date")
  );

  const minApplyOn = React.useMemo(() => {
    const now = new Date(getJSTCurrentTime("date"));
    return new Date(now.setDate(now.getDate() + 2)).toISOString().slice(0, 10);
  }, []);

  const maxApplyOn = React.useMemo(() => {
    const dayAfterTommorow = new Date(minApplyOn);
    const nextMonth = new Date(
      dayAfterTommorow.setMonth(dayAfterTommorow.getMonth() + 1)
    );
    return new Date(nextMonth.setDate(1)).toISOString().slice(0, 10);
  }, [minApplyOn]);

  const rangeErrorLabel = React.useMemo(() => {
    return `${minApplyOn.replace(/-/g, "/")} から ${maxApplyOn.replace(
      /-/g,
      "/"
    )} までの日付を選択してください。`;
  }, [maxApplyOn, minApplyOn]);

  const [frontApplyOnErrorLabel, setFronApplyOnErrorLabel] =
    React.useState(rangeErrorLabel);

  const [range, setRange] = React.useState<DateRanges>({
    start: true,
    end: true,
  });

  const [disableStartOn, setDisableStartOn] = React.useState(true);

  const [isGxAdmin, setIsGxAdmin] = React.useState(false);

  const [socUpdate, setSocUpate] = React.useState(false);

  // 無線番号切り替えのため
  const [carOrg, setCarOrg] = React.useState<Car | null>(null);

  const params = useParams<{ id: string }>();
  const id = Number(params.id);

  const location = useLocation();
  const navigate = useNavigate();

  const disableExcludedNumberOfCars = React.useMemo(() => {
    const thisMonth = new Date(getJSTCurrentTime("date"))
      .toISOString()
      .slice(0, 7);
    return !(
      isGxAdmin &&
      evCar.usageStartOn?.slice(0, 7) === thisMonth &&
      evCar.usageEndOn?.slice(0, 7) === thisMonth
    );
  }, [evCar.usageEndOn, evCar.usageStartOn, isGxAdmin]);

  const handleChangeStartDate = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setEvCar((vehicleData) => ({
        ...vehicleData,
        usageStartOn: newDate,
      }));

      setErrorData({
        ...errorData,
        usageStartOn: false,
        usageEndOn: false,
      });

      if (inRange != null && newDate != null && newDate.length !== 0) {
        const minEndDate = new Date(newDate);
        const endDate = new Date(evCar.usageEndOn ?? minEndDate);

        setRange({
          start: inRange,
          end: minEndDate.getTime() <= endDate.getTime(),
        });

        setMinUsageEndOn(minEndDate.toISOString().slice(0, 10));
      }
    },
    [errorData, evCar.usageEndOn]
  );

  const handleChangeEndDate = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setEvCar({
        ...evCar,
        usageEndOn: newDate,
      });
      setErrorData({ ...errorData, usageEndOn: false });
      inRange != null && setRange({ ...range, end: inRange });
    },
    [errorData, range, evCar]
  );

  const generateDateHandler = React.useCallback(
    (key: "back" | "front", target: "BaseDate" | "ApplyOn") => {
      return (newDate?: string) => {
        setErrorData({
          ...errorData,
          [`${key}DriverWorkCycle${target}`]: false,
        });

        setEvCar({
          ...evCar,
          [`${key}DriverWorkCycle${target}`]: newDate,
        });

        if (target === "BaseDate") {
          return;
        }

        const setDefaultLabel =
          !newDate?.length || (minApplyOn <= newDate && newDate <= maxApplyOn);
        if (key === "front") {
          setFronApplyOnErrorLabel(
            setDefaultLabel ? FRONT_APPLY_ON_DEFAULT_ERROR : rangeErrorLabel
          );
        }
      };
    },
    [errorData, evCar, maxApplyOn, minApplyOn, rangeErrorLabel]
  );

  const handleChangeFrontBaseDate = React.useMemo(
    () => generateDateHandler("front", "BaseDate"),
    [generateDateHandler]
  );
  const handleChangeFrontApplyOn = React.useMemo(
    () => generateDateHandler("front", "ApplyOn"),
    [generateDateHandler]
  );
  const handleChangeBackBaseDate = React.useMemo(
    () => generateDateHandler("back", "BaseDate"),
    [generateDateHandler]
  );

  const handleChangeEvCarModel = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, val: EvCarModel | null) => {
      setEvCar({
        ...evCar,
        evCarModelId: val?.id,
        evCarModels: val ?? undefined,
      });
      setErrorData({
        ...errorData,
        evCarModels: false,
      });
    },
    [errorData, evCar]
  );

  const handleChangePreviousEvCar = React.useCallback(
    (_: unknown, val: Car | null) =>
      setEvCar((prev) => ({
        ...prev,
        previousEvCarId: val?.evCars?.id,
        previousEvCar: val ?? undefined,
      })),
    []
  );

  const handleChangeFrontCycle = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, val: DriverWorkCycle | null) => {
      setEvCar({
        ...evCar,
        frontDriverWorkCycleId: val?.id,
        frontDriverWorkCycle: val ?? undefined,
      });
      setErrorData({
        ...errorData,
        frontDriverWorkCycle: false,
      });
    },
    [errorData, evCar]
  );

  const handleChangeBackCycle = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, val: DriverWorkCycle | null) => {
      setEvCar({
        ...evCar,
        backDriverWorkCycleId: val?.id,
        backDriverWorkCycle: val ?? undefined,
      });
      setErrorData({
        ...errorData,
        backDriverWorkCycle: false,
      });
    },
    [errorData, evCar]
  );

  const handleChangeSocPersent = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setErrorData({
        ...errorData,
        evCarSoc: false,
      });
      if (!evCar.evCarSoc) {
        return;
      }

      setEvCar({
        ...evCar,
        evCarSoc: {
          ...evCar.evCarSoc,
          socPercent: Number(e.target.value),
        },
      });
      setSocUpate(true);
    },
    [errorData, evCar]
  );

  const handleChangeIMEI = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (/^[0-9]{0,15}$/.test(e.target.value)) {
        setEvCar({
          ...evCar,
          imei: e.target.value,
        });
      }
      setErrorData({
        ...errorData,
        imei: false,
      });
    },
    [errorData, evCar]
  );

  const handleChangeIMEI2 = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (/^[0-9]{0,15}$/.test(e.target.value)) {
        setEvCar({
          ...evCar,
          imei2: e.target.value?.length ? e.target.value : undefined,
        });
      }
      setErrorData({
        ...errorData,
        imei2: false,
      });
    },
    [errorData, evCar]
  );

  const handleChangeVin = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (/^[A-Z0-9]*$/.test(e.target.value)) {
        setEvCar({
          ...evCar,
          vin: e.target.value,
        });
      }
      setErrorData({
        ...errorData,
      });
    },
    [errorData, evCar]
  );

  const handleChangeSubsidy = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, val: Subsidy | null) => {
      setSubSidySelected(val);
      setEvCar({
        ...evCar,
        subsidyType: val?.id != null ? String(val?.id) : undefined,
      });
      setErrorData({
        ...errorData,
        subsidyType: false,
      });
    },
    [errorData, evCar]
  );

  const handleChangeChargeCard = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, val: ChargeCard | null) => {
      setChargeCardSelected(val);
      setErrorData({
        ...errorData,
        chargeCardEvs: false,
      });
    },
    [errorData]
  );

  const handleChangeCause = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setCause(e.target.value);
      setErrorData({
        ...errorData,
        cause: false,
      });
    },
    [errorData]
  );

  const handleChangeGochargeCard = React.useCallback(
    (e: React.SyntheticEvent<Element, Event>, val: GochargeCard | null) => {
      setGochargeCardSelected(val);
      setErrorData({
        ...errorData,
        gochargeCardStartOn: false,
        gochargeCardEndOn: false,
      });

      const defaultStartDate = gochargeCardsWithDefault.find(
        ({ gochargeCard }) => gochargeCard.id === val?.id
      )?.defaultStartDate;

      setGochargeCardStartOn(val == null ? "" : defaultStartDate ?? "");
      setGochargeCardEndOn("");
      setGochargeCardEndInRange(true);
    },
    [errorData, gochargeCardsWithDefault]
  );

  const handleChangeGochargeCardStartDate = React.useCallback(
    (newDate?: string) => {
      setGochargeCardStartOn(newDate ?? "");
      setErrorData({
        ...errorData,
        gochargeCardStartOn: false,
        gochargeCardEndOn: false,
      });

      const inRange =
        !newDate || !gochargeCardEndOn
          ? true
          : new Date(newDate).getTime() <=
            new Date(gochargeCardEndOn).getTime();
      setGochargeCardEndInRange(inRange);
    },
    [errorData, gochargeCardEndOn]
  );

  const handleChangeGochargeCardEndDate = React.useCallback(
    (newDate?: string, inRange?: boolean) => {
      setGochargeCardEndOn(newDate ?? "");
      setErrorData({
        ...errorData,
        gochargeCardEndOn: false,
      });
      inRange != null && setGochargeCardEndInRange(inRange);
    },
    [errorData]
  );

  const handleChangeExcludedFromNumberOfCars = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEvCar((prev) => ({
        ...prev,
        excludedFromNumberOfCars: e.target.checked,
      }));
    },
    []
  );

  const handleChangeLocation = React.useCallback(
    (
      _: React.SyntheticEvent<Element, Event>,
      value: OfficeLocation | null | undefined
    ) => {
      setEvCar((pre) => ({
        ...pre,
        officeLocationsId: value?.id,
        officeLocations: value ?? undefined,
      }));
    },
    []
  );

  const handleChangeReplaceableCars = React.useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Car | null) => {
      setEvCar((pre) => ({
        ...pre,
        carId: value?.id,
      }));
      setCar(value ?? null);
    },
    []
  );

  const isShortPeriod = React.useMemo(() => {
    if (!evCar.usageEndOn) {
      return false;
    }

    const startDate = getShortDate(evCar.usageStartOn);
    const endDate = getShortDate(evCar.usageEndOn);
    const sixYearByStartDate = new Date(
      startDate.setFullYear(startDate.getFullYear() + NUMBER_OF_YEAR_WARNING)
    );

    return endDate.getTime() < sixYearByStartDate.getTime();
  }, [evCar.usageEndOn, evCar.usageStartOn]);

  const isPrevCarSet =
    carOrg?.evCars?.previousEvCarId == null && evCar?.previousEvCarId != null;

  // 詳細画面遷移
  const goToDetail = React.useCallback(
    (carId?: number) =>
      navigate(`/cars/${carId ?? id}`, {
        state: location.state,
      }),
    [id, location.state, navigate]
  );

  const [loading, setLoading] = React.useState(false);
  const upSert = React.useCallback(async () => {
    if (loading) {
      return;
    }
    setLoading(true);
    const chargeCardEvs =
      chargeCardSelected == null
        ? null
        : evCar.chargeCardEvs != null
          ? {
            ...evCar.chargeCardEvs,
            cause,
            chargeCardId: chargeCardSelected.id,
          }
          : {
            cause,
            chargeCardId: chargeCardSelected.id,
            version: 0,
          };

    const gochargeCardEv =
      gochargeCardSelected == null
        ? undefined
        : {
          gochargeCardId: gochargeCardSelected.id,
          startOn: gochargeCardStartOn,
          endOn: gochargeCardEndOn || undefined,
        };

    const params = {
      ...evCar,
      backDriverWorkCycleApplyOn: evCar.frontDriverWorkCycleApplyOn,
      chargeCardEvs,
      gochargeCardEv,
      socUpdate,
    } as EvCarRequest;

    (evCar.id == null ? createEvCarAPI(params) : editEvCarAPI(evCar.id, params))
      .then(({ data }) => goToDetail(data.carId))
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([404, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Replace}${Messages.ErrorOccurred}: ${(e as AxiosError<ErrorResponse>)?.response?.data?.message ?? ""
            }`
          );
        }

        console.log("===Error occurred while update/create EvCar===", e);
      })
      .finally(() => setLoading(false));
  }, [
    cause,
    chargeCardSelected,
    evCar,
    goToDetail,
    loading,
    socUpdate,
    gochargeCardSelected,
    gochargeCardStartOn,
    gochargeCardEndOn,
  ]);

  // Handle submit, check validate and fetch API update
  const onSubmit = React.useCallback(() => {
    // Check validate with each field
    const frontIsSameCycle =
      workingCopy?.frontDriverWorkCycleId === evCar?.frontDriverWorkCycleId &&
      workingCopy?.frontDriverWorkCycleBaseDate ===
      evCar?.frontDriverWorkCycleBaseDate &&
      workingCopy?.frontDriverWorkCycleApplyOn ===
      evCar?.frontDriverWorkCycleApplyOn;

    const backIsSameCycle =
      workingCopy?.backDriverWorkCycleId === evCar?.backDriverWorkCycleId &&
      workingCopy?.backDriverWorkCycleBaseDate ===
      evCar?.backDriverWorkCycleBaseDate &&
      workingCopy?.backDriverWorkCycleApplyOn ===
      evCar?.backDriverWorkCycleApplyOn;

    const errorDataTemp: EvCarError = {
      evCarModels: !evCar.evCarModels,
      usageStartOn: !evCar.usageStartOn || !range.start,
      usageEndOn: !range.end,
      subsidyType: evCar.subsidyType == null,
      imei: evCar?.imei?.length !== 15,
      imei2: (evCar?.imei2 ?? "") !== "" && evCar?.imei2?.length !== 15,
      evCarSoc:
        evCar.evCarSoc != null && isGxAdmin && isNaN(evCar.evCarSoc.socPercent),
      frontDriverWorkCycle: !evCar.frontDriverWorkCycle,
      frontDriverWorkCycleBaseDate: !evCar.frontDriverWorkCycleBaseDate,
      frontDriverWorkCycleApplyOn:
        !evCar.frontDriverWorkCycleApplyOn ||
        ((!backIsSameCycle || !frontIsSameCycle) &&
          (evCar.frontDriverWorkCycleApplyOn < minApplyOn ||
            evCar.frontDriverWorkCycleApplyOn > maxApplyOn)),
      backDriverWorkCycle: !evCar.backDriverWorkCycle,
      backDriverWorkCycleBaseDate: !evCar.backDriverWorkCycleBaseDate,
      gochargeCardStartOn: gochargeCardSelected != null && !gochargeCardStartOn,
      gochargeCardEndOn: gochargeCardSelected != null && !gochargeCardEndInRange,
    };

    // If have error then return and show error
    if (Object.values(errorDataTemp).some((error) => error)) {
      setErrorData(errorDataTemp);
      return;
    }

    setErrorData({});

    if (isShortPeriod) {
      setShowShortPeriodWarning(true);
      return;
    }

    if (isPrevCarSet) {
      setShowSetPerviousCarWarning(true);
      return;
    }

    upSert();
  }, [
    evCar,
    maxApplyOn,
    minApplyOn,
    range.end,
    range.start,
    upSert,
    workingCopy,
    isGxAdmin,
    isShortPeriod,
    isPrevCarSet,
    gochargeCardSelected,
    gochargeCardStartOn,
    gochargeCardEndInRange,
  ]);

  const getCarInfo = React.useCallback(
    (isAdmin: boolean) => {
      getCarAPI(Number(id))
        .then(async (res) => {
          const car = res.data;

          setCar(car);
          setCarOrg(car);

          const cycles =
            car.evCars?.id != null
              ? await getLinkedDriverWorkCycles(car.evCars.id)
              : null;

          const initialEvCars: Partial<EvCar> = car?.evCars ?? {
            carId: car.id,
            usageStartOn: isAdmin ? getJSTCurrentTime("date") : "2030-12-31",
            frontDriverWorkCycleApplyOn: minApplyOn,
            backDriverWorkCycleApplyOn: minApplyOn,
            excludedFromNumberOfCars: false,
            subsidyType: "1",
            version: 0,
          };
          if (cycles?.data.evCarId != null) {
            initialEvCars.frontDriverWorkCycle =
              cycles.data.frontDriverWorkCycles;
            initialEvCars.backDriverWorkCycle =
              cycles.data.backDriverWorkCycles;
          }

          setEvCar(initialEvCars);
          setWorkingCopy(car?.evCars ? { ...car.evCars } : undefined);

          setCause(car?.evCars?.chargeCardEvs?.cause ?? "");

          setDisableStartOn(
            car.evCars?.usageStartOn != null &&
            new Date(car.evCars.usageStartOn) < new Date(minUsageStartOn)
          );

          if (car?.evCars?.usageStartOn != null) {
            const defaultStartDate = new Date(car.evCars.usageStartOn);
            setMinUsageEndOn(
              new Date(defaultStartDate).toISOString().slice(0, 10)
            );
          }

          if (car?.evCars?.chargeCardEvs?.chargeCards) {
            setChargeCardSelected(car.evCars.chargeCardEvs.chargeCards);
          }

          const gochargeCardEv = car?.evCars?.gochargeCardEv;
          setGochargeCardLocked(gochargeCardEv != null);
          setGochargeCardSelected(gochargeCardEv?.gochargeCards ?? null);
          setGochargeCardStartOn(gochargeCardEv?.startOn ?? "");
          setGochargeCardEndOn(gochargeCardEv?.endOn ?? "");

          return {
            officeId: car.officeId,
            subsidyType: car?.evCars?.subsidyType ?? "1",
            chargeCardSelected: car?.evCars?.chargeCardEvs?.chargeCards,
            gochargeCardEv,
          };
        })
        .then(({ officeId, subsidyType, chargeCardSelected, gochargeCardEv }) => {
          getDriverWorkCyclesAPI([officeId])
            .then((res) => {
              setFrontDriverWorkCycles(res.data ? [...res.data] : []);
              setBackDriverWorkCycles(res.data ? [...res.data] : []);
            })
            .catch((e) => console.log("===Error when get Cycle===", e));

          getChargeCardsAPI([officeId])
            .then((res) => {
              const chargeCards =
                chargeCardSelected == null
                  ? res.data
                  : Array.from(
                    new Map(
                      [chargeCardSelected, ...res.data].map((e) => [e.id, e])
                    ).values()
                  );

              setChargeCards(
                chargeCards.filter((card) => !card.isOfficeMaster)
              );
            })
            .catch((e) => console.log("===Error when get ChargeCards==="));

          getGochargeCardsAPI(officeId)
            .then((res) => {
              const gochargeCards =
                gochargeCardEv?.gochargeCards == null
                  ? res.data
                  : Array.from(
                    new Map(
                      [
                        {
                          gochargeCard: gochargeCardEv.gochargeCards,
                          defaultStartDate: gochargeCardEv.startOn,
                        },
                        ...res.data,
                      ].map((e) => [e.gochargeCard.id, e])
                    ).values()
                  );

              setGochargeCardsWithDefault(gochargeCards);
            })
            .catch((e) => console.log("===Error when get GochargeCards==="));

          getSubsidiesAPI()
            .then((res) => {
              if (res.data?.length) {
                setSubSidySelected(
                  res.data.find(({ id }) => String(id) === subsidyType) ?? null
                );
              }
              setSubsidies(res.data ?? []);
            })
            .catch((e) => console.log("===Error when get subsidy=="));

          getParkingLotsAPI(officeId)
            .then(({ data }) => setParkingLots(data))
            .catch((e) =>
              console.log("===Error occurred while getting ParkingLots===", e)
            );
        })
        .catch((e) => console.log("===Error when get car===", e));
    },
    [id, minApplyOn, minUsageStartOn]
  );

  // Load vehicle detail when id change
  React.useEffect(() => {
    getLoginUserAPI()
      .then((res) => {
        setIsGxAdmin(res.data.role === GX_ADMIN);
        return res.data.role === GX_ADMIN;
      })
      .then(getCarInfo)
      .catch((e) =>
        console.log("==Error occurred while getting userInfo==", e)
      );

    getEvCarModelsAPI()
      .then((res) => setEvCarModels(res.data ?? []))
      .catch((e) => console.log("===Error when get evCarModels==="));
  }, [getCarInfo, id]);

  // 稼働台数カウント対象外チェックが非活性化したらチェックを外す
  React.useEffect(() => {
    if (disableExcludedNumberOfCars) {
      setEvCar((prev) => ({
        ...prev,
        excludedFromNumberOfCars: false,
      }));
    }
  }, [disableExcludedNumberOfCars]);

  // 付け替え可能な車両一覧を取得する
  React.useEffect(() => {
    let ignore = false;
    if (params.id == null || carOrg == null) {
      return;
    }

    getReplaceableCarsAPI(id)
      .then(({ data }) => {
        !ignore &&
          setReplaceableCars(
            Array.from(
              new Map([carOrg, ...data].map((e) => [e.id, e])).values()
            )
          );
      })
      .catch((e) =>
        console.log("===Error occurred while getting replaceable cars===", e)
      );

    return () => {
      ignore = true;
    };
  }, [params.id, id, carOrg]);

  // 付け替え元車両として選択可能なEV車両一覧を取得する
  React.useEffect(() => {
    let ignore = false;
    if (params?.id == null || car == null) {
      return;
    }

    getSelectablePreviousCars(Number(params.id))
      .then(({ data }) => {
        const next = [...data];
        car.evCars?.previousCar && next.push(car.evCars.previousCar);
        next.sort(
          (a, b) => a.vehicleCode?.localeCompare(b.vehicleCode ?? "") ?? 0
        );
        !ignore && setPreviousCars(next);
      })
      .catch((e) =>
        console.error(
          "===Error occurred while getting selectable previous cars===",
          e
        )
      );

    return () => {
      ignore = true;
    };
  }, [params, car]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{VehicleConstants.Edit}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                }}
                className="gx-info-table"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: "none" }}>
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 20,
                        }}
                        text={VehicleConstants.BasicInfomation}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={VehicleConstants.CompanyOffice}
                        value={car?.office?.displayName ?? ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={isGxAdmin && evCar.id != null ? undefined : { pb: 0 }}
                      className={
                        isGxAdmin && evCar?.id != null ? "required" : undefined
                      }
                    >
                      {isGxAdmin && evCar?.id != null ? (
                        <Autocomplete
                          options={replaceableCars}
                          label={VehicleConstants.RadioNumber}
                          value={car}
                          onChange={handleChangeReplaceableCars}
                          getOptionLabel={getVehicleCodeLabel}
                          isOptionEqualToValue={(opt, val) =>
                            opt?.id === val?.id
                          }
                          sx={{ width: "80%" }}
                          disableClearable
                        />
                      ) : (
                        <TextField
                          label={VehicleConstants.RadioNumber}
                          value={getVehicleCodeLabel(car ?? undefined)}
                          InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                          }}
                          sx={{ width: "100%", wordBreak: "break-all" }}
                          multiline
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      {isGxAdmin && evCar?.previousCar == null ? (
                        <Autocomplete
                          options={previousCars}
                          label={VehicleConstants.PreviousCar}
                          value={evCar?.previousCar ?? null}
                          getOptionLabel={getVehicleCodeAndEvCarLabel}
                          isOptionEqualToValue={(opt, val) => opt.id === val.id}
                          onChange={handleChangePreviousEvCar}
                          sx={{ width: "80%" }}
                        />
                      ) : (
                        <TextField
                          label={VehicleConstants.PreviousCar}
                          value={getVehicleCodeAndEvCarLabel(
                            evCar?.previousCar
                          )}
                          InputProps={{
                            readOnly: true,
                            disableUnderline: true,
                          }}
                          sx={{ width: "80%", wordBreak: "break-all" }}
                          multiline
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={VehicleConstants.PlateNumber}
                        value={car?.plateNumberFullText ?? ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ pb: 0 }}>
                      <TextField
                        label={VehicleConstants.CarModelName}
                        value={car?.carModels?.displayName ?? ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <Autocomplete
                        options={evCarModels}
                        label={VehicleConstants.EVModel}
                        error={!!errorData.evCarModels}
                        errorLabel={
                          VehicleConstants.EVModel + Messages.PleaseEnter
                        }
                        value={evCar?.evCarModels ?? null}
                        onChange={handleChangeEvCarModel}
                        getOptionLabel={(opt) => opt?.name || ""}
                        isOptionEqualToValue={(option, value) => {
                          return option?.id === value?.id;
                        }}
                        sx={{ width: "80%" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <Picker
                        label={
                          VehicleConstants.ChargePlanningSystemUsageStartDate
                        }
                        type="date"
                        minDate={minUsageStartOn}
                        value={evCar.usageStartOn ?? ""}
                        decode={(out) => out.slice(0, 10)}
                        onChange={handleChangeStartDate}
                        error={!!errorData.usageStartOn}
                        errorLabel={
                          VehicleConstants.ChargePlanningSystemUsageStartDate +
                          Messages.PleaseEnter
                        }
                        replaceRangeErrorLabel
                        disabled={!isGxAdmin || disableStartOn}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Picker
                        label={
                          VehicleConstants.ChargePlanningSystemUsageEndDate
                        }
                        type="date"
                        minDate={minUsageEndOn}
                        value={evCar.usageEndOn ?? ""}
                        onChange={handleChangeEndDate}
                        decode={(out) => out.slice(0, 10)}
                        error={!!errorData.usageEndOn}
                        errorLabel={
                          VehicleConstants.ChargePlanningSystemUsageEndDate +
                          Messages.PleaseEnter
                        }
                        replaceRangeErrorLabel
                        disabled={!isGxAdmin}
                      />
                    </TableCell>
                  </TableRow>
                  {isGxAdmin && (
                    <TableRow>
                      <TableCell>
                        <FormControl>
                          <FormLabel sx={{ fontSize: "0.75rem" }}>
                            {`${VehicleConstants.NumberOfCars}`}
                          </FormLabel>
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  style={{ fontSize: "10px" }}
                                  checked={evCar.excludedFromNumberOfCars}
                                  onChange={
                                    handleChangeExcludedFromNumberOfCars
                                  }
                                  disabled={disableExcludedNumberOfCars}
                                />
                              }
                              label={`${VehicleConstants.NumberOfCars}${VehicleConstants.Exclude}`}
                            />
                          </FormGroup>
                        </FormControl>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell>
                      <Autocomplete
                        options={chargeCards}
                        label={VehicleConstants.ChargeCardControlNumber}
                        error={!!errorData.chargeCardEvs}
                        errorLabel={
                          VehicleConstants.ChargeCardControlNumber +
                          Messages.PleaseEnter
                        }
                        value={chargeCardSelected}
                        onChange={handleChangeChargeCard}
                        getOptionLabel={(opt) => opt?.managementNumber || ""}
                        isOptionEqualToValue={(option, value) => {
                          return option?.id === value?.id;
                        }}
                        sx={{ width: "80%" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={VehicleConstants.ChargeCardRemarks}
                        error={!!errorData.cause}
                        errorLabel={
                          VehicleConstants.ChargeCardRemarks +
                          Messages.PleaseEnter
                        }
                        value={cause}
                        onChange={handleChangeCause}
                        inputProps={{ style: { marginLeft: 10 } }}
                        disabled={!chargeCardSelected}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <Autocomplete
                          options={gochargeCardOptions}
                          label={VehicleConstants.GochargeCard}
                          value={gochargeCardSelected}
                          onChange={handleChangeGochargeCard}
                          getOptionLabel={(opt) =>
                            opt?.externalChargeCardNo || ""
                          }
                          isOptionEqualToValue={(option, value) =>
                            option?.id === value?.id
                          }
                          sx={{
                            width: "220px",
                            "& .MuiInputBase-input": { fontWeight: "bold" },
                          }}
                          disabled={gochargeCardLocked}
                        />
                        <Box
                          className={
                            gochargeCardSelected ? "required" : undefined
                          }
                        >
                          <Picker
                            label={VehicleConstants.GochargeCardStartOn}
                            type="date"
                            value={gochargeCardStartOn}
                            decode={(out) => out.slice(0, 10)}
                            onChange={handleChangeGochargeCardStartDate}
                            error={!!errorData.gochargeCardStartOn}
                            errorLabel={
                              VehicleConstants.GochargeCardStartOn +
                              Messages.PleaseEnter
                            }
                            disabled={!gochargeCardSelected}
                          />
                        </Box>
                        <Picker
                          label={VehicleConstants.GochargeCardEndOn}
                          type="date"
                          minDate={gochargeCardStartOn || undefined}
                          value={gochargeCardEndOn}
                          decode={(out) => out.slice(0, 10)}
                          onChange={handleChangeGochargeCardEndDate}
                          error={!!errorData.gochargeCardEndOn}
                          errorLabel={
                            VehicleConstants.GochargeCardEndOn +
                            Messages.PleaseEnter
                          }
                          replaceRangeErrorLabel
                          disabled={!gochargeCardStartOn}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <Autocomplete
                        options={subsidies}
                        label={VehicleConstants.Subsidy}
                        error={!!errorData.subsidyType}
                        errorLabel={
                          VehicleConstants.Subsidy + Messages.PleaseEnter
                        }
                        value={subsidySelected}
                        onChange={handleChangeSubsidy}
                        getOptionLabel={(opt) => opt.displayName || ""}
                        isOptionEqualToValue={(option, value) => {
                          return option.id === value.id;
                        }}
                        sx={{ width: "80%" }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                }}
                className="gx-info-table"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: "none" }}>
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 20,
                        }}
                        text={VehicleConstants.SocAcquisitionInformation}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <Box sx={{ display: "flex", alignItems: "baseline" }}>
                        <TextField
                          label={VehicleConstants.IMEI}
                          error={!!errorData.imei}
                          errorLabel={VehicleConstants.IMEIErrorMessage}
                          value={evCar.imei}
                          onChange={handleChangeIMEI}
                          inputProps={{ style: { marginLeft: 10 } }}
                        />
                        <Typography variant="caption" sx={{ marginLeft: 2 }}>
                          {VehicleConstants.IMEIUpdateCaution}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={VehicleConstants.IMEI2}
                        error={!!errorData.imei2}
                        errorLabel={VehicleConstants.IMEIErrorMessage}
                        value={evCar.imei2}
                        onChange={handleChangeIMEI2}
                        inputProps={{ style: { marginLeft: 10 } }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={VehicleConstants.VIN}
                        value={evCar.vin}
                        onChange={handleChangeVin}
                        inputProps={{ style: { marginLeft: 10 } }}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                }}
                className="gx-info-table"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: "none" }}>
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 20,
                        }}
                        text={VehicleConstants.SocInformation}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      className={
                        evCar.evCarSoc != null && isGxAdmin ? "required" : ""
                      }
                    >
                      <TextField
                        label={VehicleConstants.ResidualPowerPercent}
                        error={!!errorData.evCarSoc}
                        errorLabel={
                          VehicleConstants.ResidualPowerPercent +
                          Messages.PleaseEnter
                        }
                        type="number"
                        value={evCar.evCarSoc?.socPercent?.toString()}
                        onChange={handleChangeSocPersent}
                        disabled={!evCar?.evCarSoc || !isGxAdmin}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={VehicleConstants.SocLastCollectedAt}
                        value={lastCollectedAt(
                          "full",
                          evCar.evCarSoc?.lastCollectedAt
                        )}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        sx={{ width: "100%", wordBreak: "break-all" }}
                        disabled={!evCar?.evCarSoc}
                        multiline
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Table
                sx={{
                  borderCollapse: "separate",
                  borderSpacing: "0px 4px",
                  width: "80%",
                }}
                className="gx-info-table-no-width"
              >
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ border: "none" }}>
                      <TitleGroup
                        style={{
                          marginBottom: 0,
                          marginTop: 20,
                        }}
                        text={VehicleConstants.WorkCycle}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required" colSpan={2}>
                      <Picker
                        label={VehicleConstants.FrontApplyOn}
                        value={evCar.frontDriverWorkCycleApplyOn ?? ""}
                        type="date"
                        onChange={handleChangeFrontApplyOn}
                        decode={(out) => out.slice(0, 10)}
                        error={!!errorData.frontDriverWorkCycleApplyOn}
                        errorLabel={frontApplyOnErrorLabel}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <AutocompleteMUI<DriverWorkCycle>
                        options={frontDriverWorkCycles}
                        value={evCar.frontDriverWorkCycle ?? null}
                        onChange={handleChangeFrontCycle}
                        getOptionLabel={(opt) => opt?.shortName || ""}
                        isOptionEqualToValue={(option, value) => {
                          return option?.id === value?.id;
                        }}
                        renderInput={(params) => {
                          return (
                            <TextField
                              {...params}
                              inputProps={{
                                style: { marginLeft: 10 },
                                ...params.inputProps,
                              }}
                              label={VehicleConstants.Front}
                              sx={{ border: "none" }}
                              error={!!errorData.frontDriverWorkCycle}
                              errorLabel={
                                VehicleConstants.Front + Messages.PleaseEnter
                              }
                              minWidth="100%"
                            />
                          );
                        }}
                        renderOption={(props, option) => {
                          const optionStr = `${option.shortName}: ${option?.driverWorkShifts
                            ?.map(({ shortName }) => shortName)
                            .join(",") ?? ""
                            }`;
                          return (
                            <li {...props}>
                              <div>
                                {optionStr.length <= 20
                                  ? optionStr
                                  : `${optionStr.slice(0, 21)}...`}
                              </div>
                            </li>
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 480 }} className="required">
                      <Picker
                        label={VehicleConstants.FrontBaseDate}
                        value={evCar.frontDriverWorkCycleBaseDate ?? ""}
                        type="date"
                        onChange={handleChangeFrontBaseDate}
                        decode={(out) => out.slice(0, 10)}
                        error={!!errorData.frontDriverWorkCycleBaseDate}
                        errorLabel={`${VehicleConstants.FrontBaseDate}${Messages.PleaseEnter}`}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      {backDriverWorkCycles && (
                        <AutocompleteMUI<DriverWorkCycle>
                          options={backDriverWorkCycles}
                          value={evCar.backDriverWorkCycle ?? null}
                          onChange={handleChangeBackCycle}
                          getOptionLabel={(opt) => opt?.shortName || ""}
                          isOptionEqualToValue={(option, value) => {
                            return option?.id === value?.id;
                          }}
                          renderInput={(params) => {
                            return (
                              <TextField
                                {...params}
                                inputProps={{
                                  style: { marginLeft: 10 },
                                  ...params.inputProps,
                                }}
                                label={VehicleConstants.Rear}
                                sx={{ border: "none" }}
                                error={!!errorData.backDriverWorkCycle}
                                errorLabel={
                                  VehicleConstants.Rear + Messages.PleaseEnter
                                }
                                minWidth="100%"
                              />
                            );
                          }}
                          renderOption={(props, option) => {
                            const optionStr = `${option.shortName}: ${option?.driverWorkShifts
                              ?.map(({ shortName }) => shortName)
                              .join(",") ?? ""
                              }`;
                            return (
                              <li {...props}>
                                <div>
                                  {optionStr.length <= 20
                                    ? optionStr
                                    : `${optionStr.slice(0, 21)}...`}
                                </div>
                              </li>
                            );
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell className="required">
                      <Picker
                        label={VehicleConstants.BackBaseDate}
                        value={evCar.backDriverWorkCycleBaseDate ?? ""}
                        type="date"
                        onChange={handleChangeBackBaseDate}
                        decode={(out) => out.slice(0, 10)}
                        error={!!errorData.backDriverWorkCycleBaseDate}
                        errorLabel={`${VehicleConstants.BackBaseDate}${Messages.PleaseEnter}`}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              {isGxAdmin && (
                <Table
                  sx={{
                    borderCollapse: "separate",
                    borderSpacing: "0px 4px",
                  }}
                  className="gx-info-table"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ border: "none" }}>
                        <TitleGroup
                          style={{
                            marginBottom: 0,
                            marginTop: 20,
                          }}
                          text={VehicleConstants.ParkingLot}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <Autocomplete
                          options={parkingLots}
                          value={evCar.officeLocations}
                          getOptionLabel={(opt) => opt?.name ?? ""}
                          label={VehicleConstants.ParkingLot}
                          sx={{ width: "80%" }}
                          disabled={!parkingLots.length}
                          onChange={handleChangeLocation}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
              <ActionButtons
                loading={loading}
                onCancel={goToDetail}
                onSubmit={onSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
      <ConfirmDialog
        visible={showShortPeriodWarning}
        headerTitle={VehicleConstants.Confirm}
        showIcon
        type="Normal"
        onCancel={() => setShowShortPeriodWarning(false)}
        onOk={() => {
          setShowShortPeriodWarning(false);
          if (isPrevCarSet) {
            setShowSetPerviousCarWarning(true);
            return;
          }
          upSert();
        }}
      >
        <Typography gutterBottom>{VehicleConstants.PeriodWarning}</Typography>
      </ConfirmDialog>
      <ConfirmDialog
        visible={showSetPreviousCarWarning}
        headerTitle={VehicleConstants.Confirm}
        showIcon
        type="Normal"
        onCancel={() => setShowSetPerviousCarWarning(false)}
        onOk={() => {
          setShowSetPerviousCarWarning(false);
          upSert();
        }}
      >
        <Typography gutterBottom sx={{ whiteSpace: "pre-line" }}>
          {getSetPreviousCarWarnMessage(evCar)}
        </Typography>
      </ConfirmDialog>
    </React.Fragment>
  );
};
